import { useEffect, useRef, useState, type ReactNode } from 'react';

import { Box, CircularProgress, Link, Typography } from '@mui/material';
import { Link as RouterLink, Navigate, useLocation, useNavigate } from 'react-router-dom';

import { COLORS } from '../IndividualPlan/tokens';
import { ProgramEvaluationShell } from './ProgramEvaluationShell';
import { logEvent } from '../../utils/eventLog';
import { ScoreUnitProvider, useScoreUnit } from './ScoreUnitContext';
import { DataNotes } from './components/DataNotes';
import { refreshPlansGeneratedMarker, usePlansStatus } from '../Plans/plansGenerated';
import { GenerationPanel, RegeneratePanel } from './components/GenerationPanel';
import { ProgramEvaluationHeader } from './components/ProgramEvaluationHeader';
import { ScoreUnitToggle } from './components/ScoreUnitToggle';
import {
  QUEUE_EXPIRED_ERROR,
  RETRY_WINDOW_CLOSED_ERROR,
  useProgramEvaluationGeneration,
  type PeInputsProvider,
} from './generation-hook';
import { useProgramEvaluation, type PeDataSource } from './program-evaluation-hook';
import {
  DistrictHeader,
  SectionEyebrow,
  SectionSlot,
  ToggleBar,
} from './program-evaluation.styled';
import { ExecutiveSummary } from './sections/ExecutiveSummary';
import { GoalsAccountability } from './sections/GoalsAccountability';
import { PatternsWorthInvestigating } from './sections/PatternsWorthInvestigating';
import { AssessmentDataAbsentBanner } from './components/AssessmentDataAbsentNote';
import { ProgramPerformance } from './sections/ProgramPerformance';
import { SchoolBySchool } from './sections/SchoolBySchool';
import { districtSchoolTotals } from './components/SchoolDrilldownTable';
import { SubgroupFindings } from './sections/SubgroupFindings';
import {
  findSection,
  reportSettings,
  reportThresholds,
  type PeContent,
  type PeReport,
  type ScoreUnit,
} from './types';
import { hasResidualSubgroupPatterns, schoolResidualCount } from './utils/residuals';
import { schoolYearStartYear } from './utils/schoolYear';

const SECTION_SLOTS: ReadonlyArray<{ id: string; title: string }> = [
  { id: 'executive-summary', title: 'Executive Summary' },
  { id: 'program-performance', title: 'Program Performance' },
  { id: 'school-by-school', title: 'School-by-School' },
  { id: 'subgroup-findings', title: 'Cohort Findings' },
  { id: 'patterns-worth-investigating', title: 'Patterns Worth Investigating' },
  { id: 'goals-accountability', title: 'Goals & Accountability' },
];

type ProgramEvaluationProps = {
  source?: PeDataSource;
  inputsProvider?: PeInputsProvider;
  latestDataSchoolYear?: string;
  missingReportRoute?: string;
};

const missingInputsProvider: PeInputsProvider = () =>
  Promise.reject(new Error('no Program Evaluation inputs source configured'));

function PageShell({ children }: { children: ReactNode }) {
  return <ProgramEvaluationShell crumbs={[]}>{children}</ProgramEvaluationShell>;
}

function PlaceholderHeading({ id, title }: { id: string; title: string }) {
  return (
    <Typography
      id={`${id}-heading`}
      component="h2"
      sx={{ fontSize: 20, fontWeight: 700, color: COLORS.ink }}
    >
      {title}
    </Typography>
  );
}

export function ReportSections({
  report,
  content,
}: {
  report: PeReport;
  content: PeContent | null;
}) {
  const { scoreUnit } = useScoreUnit();
  const sections = content?.sections ?? [];
  const schoolFindingsText =
    (content && findSection(content, 'school_notable_findings')?.text) ?? '';
  const subgroupPatternsText =
    (content && findSection(content, 'subgroup_notable_patterns')?.text) ?? '';
  const pwiFindingsText =
    (content && findSection(content, 'patterns_worth_investigating')?.text) ?? '';
  const candidates = report.candidate_findings;
  const isSingleSchool = report.by_school.length <= 1;
  const settings = reportSettings(report);
  const drilldown = report.drilldown;
  const schoolDrilldown = drilldown
    ? { schools: drilldown.schools, totals: districtSchoolTotals(report.district, drilldown.schools), omitted: drilldown.schools_omitted }
    : undefined;

  return (
    <>
      {SECTION_SLOTS.map((slot, index) => {
        if (slot.id === 'school-by-school' && isSingleSchool) {
          return null;
        }
        const sectionNumber = String(index + 1).padStart(2, '0');
        return (
          <SectionSlot key={slot.id} aria-labelledby={`${slot.id}-heading`}>
            <SectionEyebrow data-testid="section-eyebrow">{`SECTION ${sectionNumber}`}</SectionEyebrow>
            {slot.id === 'executive-summary' && content ? (
              <ExecutiveSummary report={report} content={content} />
            ) : slot.id === 'program-performance' && content ? (
              <ProgramPerformance report={report} content={content} unit={scoreUnit} />
            ) : slot.id === 'patterns-worth-investigating' ? (
              <PatternsWorthInvestigating findingsText={pwiFindingsText} />
            ) : slot.id === 'school-by-school' ? (
              <SchoolBySchool
                schools={report.by_school}
                districtReclassificationRate={report.reclassification.this_year.rate}
                derivedDepartures={report.enrollment.departures_this_year?.derived_count ?? 0}
                dateInferredDepartures={
                  report.enrollment.departures_this_year?.date_inferred_count ?? 0
                }
                districtCompositeGrowthScaleScoreDelta={
                  report.growth.composite.district_average_scale_score_delta
                }
                districtCompositeGrowthProficiencyLevelDelta={
                  report.growth.composite.district_average_proficiency_level_delta
                }
                unit={scoreUnit}
                notableFindingsText={schoolFindingsText}
                residualCount={schoolResidualCount(
                  (candidates?.school_findings ?? []).map((f) => f.school_name),
                  schoolFindingsText,
                  scoreUnit,
                )}
                isSingleSchool={isSingleSchool}
                drilldown={schoolDrilldown}
                threshold={settings.smallGroupThreshold}
              />
            ) : slot.id === 'subgroup-findings' ? (
              <SubgroupFindings
                subgroups={report.subgroups}
                sections={sections}
                unit={scoreUnit}
                assessmentDataAbsent={report.assessment_data_absent}
                thresholds={reportThresholds(report.metadata)}
                cohorts={drilldown?.cohorts}
                schoolYear={report.district.school_year}
                lastYearKnown={report.district.el_student_count_last_year != null}
                threshold={settings.smallGroupThreshold}
                notablePatternsText={subgroupPatternsText}
                residualPatterns={hasResidualSubgroupPatterns(
                  (candidates?.subgroup_patterns ?? []).length,
                  subgroupPatternsText,
                  scoreUnit,
                )}
              />
            ) : slot.id === 'goals-accountability' ? (
              <GoalsAccountability />
            ) : (
              <PlaceholderHeading id={slot.id} title={slot.title} />
            )}
          </SectionSlot>
        );
      })}
    </>
  );
}

function ScoreUnitSync({ onChange }: { onChange: (unit: ScoreUnit) => void }) {
  const { scoreUnit } = useScoreUnit();
  useEffect(() => {
    onChange(scoreUnit);
  }, [scoreUnit, onChange]);
  return null;
}

function ReportMessage({ children }: { children: string }) {
  return (
    <PageShell>
      <Typography role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
        {children}
      </Typography>
    </PageShell>
  );
}

function NoReportEmptyState() {
  return (
    <PageShell>
      <Box
        role="alert"
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-start',
          gap: 1.5,
        }}
      >
        <Typography sx={{ fontSize: 16, color: COLORS.ink }}>
          A Program Evaluation report has not been generated yet for this district.
        </Typography>
        <Link
          component={RouterLink}
          to="/upload"
          state={{ assessment: 'WIDA ACCESS' }}
          sx={{ fontSize: 16 }}
        >
          Upload district data to generate a report
        </Link>
      </Box>
    </PageShell>
  );
}

export function ProgramEvaluation({
  source,
  inputsProvider,
  latestDataSchoolYear,
  missingReportRoute,
}: ProgramEvaluationProps) {
  const { report, content, stale, loading, error, refetch } = useProgramEvaluation(source);
  const viewLogged = useRef(false);
  useEffect(() => {
    if (viewLogged.current || loading || !report) return;
    viewLogged.current = true;
    void logEvent('program_evaluation.viewed.v1', {
      schoolYear: report.district.school_year,
      unitMode: content?.unit_mode ?? '',
      stale: String(stale),
    }).catch(() => {});
  }, [loading, report, content, stale]);
  const plansStatus = usePlansStatus();
  const plansGenerated = plansStatus?.generated === true;
  const [generationUnit, setGenerationUnit] = useState<ScoreUnit>('proficiency_level');
  const [plansRefreshFailed, setPlansRefreshFailed] = useState(false);
  const plansRefreshAttempt = useRef(0);
  const plansTrack = useRef(false);
  const clickAction = useRef<'combined' | 'report' | null>(null);
  const markerRefreshed = useRef(false);
  const clickRefresh = useRef<Promise<void> | null>(null);

  const trackedMarkerRefresh = () => {
    const attempt = ++plansRefreshAttempt.current;
    setPlansRefreshFailed(false);
    return refreshPlansGeneratedMarker()
      .then(() => {
        markerRefreshed.current = true;
      })
      .catch(() => {
        if (plansTrack.current && plansRefreshAttempt.current === attempt) {
          setPlansRefreshFailed(true);
        }
      });
  };

  const generation = useProgramEvaluationGeneration({
    inputsProvider: inputsProvider ?? missingInputsProvider,
    unitMode: generationUnit,
    schoolYear: latestDataSchoolYear,
    onReady: () => {
      if (clickAction.current !== 'report') {
        void (clickRefresh.current ?? Promise.resolve()).then(() => {
          if (!markerRefreshed.current) {
            void trackedMarkerRefresh();
          }
        });
      }
      refetch();
    },
    enabled: Boolean(inputsProvider),
  });

  const refreshMarkerFromClick = () => {
    clickAction.current = 'combined';
    markerRefreshed.current = false;
    plansTrack.current = plansGenerated;
    clickRefresh.current = trackedMarkerRefresh();
  };

  const regenerateAll = () => {
    refreshMarkerFromClick();
    generation.regenerate();
  };

  const generateAll = () => {
    refreshMarkerFromClick();
    generation.submit();
  };

  const generateReportOnly = () => {
    clickAction.current = 'report';
    generation.submit();
  };

  const retryAction =
    generation.errorClass === QUEUE_EXPIRED_ERROR ||
    generation.errorClass === RETRY_WINDOW_CLOSED_ERROR ||
    (stale && generation.phase === 'failed' && !generation.freshJob)
      ? regenerateAll
      : generation.retry;

  const location = useLocation();
  const navigate = useNavigate();
  const intentState = location.state as {
    generate?: boolean;
    regenerate?: boolean;
  } | null;
  const generateIntent = useRef(Boolean(intentState?.generate || intentState?.regenerate));
  useEffect(() => {
    if (!generateIntent.current) return;
    if (loading) return;
    const dropIntent = () => {
      generateIntent.current = false;
      navigate(location.pathname, { replace: true });
    };
    if (error !== null || !inputsProvider || generation.phase === 'error') {
      dropIntent();
      return;
    }
    if (generation.phase === 'submitting' || generation.phase === 'generating') return;
    if (!report) {
      dropIntent();
      if (generation.phase === 'idle') queueMicrotask(generateReportOnly);
      return;
    }
    if (plansStatus === null) return;
    if (content?.unit_mode && generationUnit !== content.unit_mode) return;
    dropIntent();
    const reportStartYear = schoolYearStartYear(report.district.school_year);
    const dataStartYear = latestDataSchoolYear ? schoolYearStartYear(latestDataSchoolYear) : null;
    if (reportStartYear !== null && dataStartYear !== null && dataStartYear < reportStartYear) {
      return;
    }
    if (reportStartYear !== null && dataStartYear !== null && dataStartYear > reportStartYear) {
      queueMicrotask(generateAll);
      return;
    }
    queueMicrotask(regenerateAll);
  });

  const plansRefreshAlert = plansRefreshFailed ? (
    <Typography role="alert" sx={{ fontSize: 14, color: COLORS.ink, mt: 2 }}>
      {"Your Student Action Plans weren't refreshed. Use Regenerate to try again."}
    </Typography>
  ) : null;

  if (loading) {
    return (
      <PageShell>
        <CircularProgress aria-label="Loading Program Evaluation report" />
      </PageShell>
    );
  }

  if (error) {
    return <ReportMessage>Program Evaluation is temporarily unavailable</ReportMessage>;
  }

  if (!report) {
    if (inputsProvider) {
      return (
        <PageShell>
          <GenerationPanel
            phase={generation.phase}
            errorClass={generation.errorClass}
            onGenerate={generateReportOnly}
            onRetry={generation.retry}
          />
        </PageShell>
      );
    }
    if (missingReportRoute) return <Navigate to={missingReportRoute} replace />;
    return <NoReportEmptyState />;
  }

  const reportStartYear = schoolYearStartYear(report.district.school_year);
  const dataStartYear = latestDataSchoolYear ? schoolYearStartYear(latestDataSchoolYear) : null;
  const newerDataAvailable =
    reportStartYear !== null && dataStartYear !== null && dataStartYear > reportStartYear;
  const olderDataUploaded =
    reportStartYear !== null && dataStartYear !== null && dataStartYear < reportStartYear;

  if (report.cannot_generate) {
    if (inputsProvider) {
      if (generation.phase === 'ready') {
        return (
          <PageShell>
            <Typography role="alert" sx={{ fontSize: 16, color: COLORS.ink }}>
              This Program Evaluation report cannot be generated.
            </Typography>
            {!olderDataUploaded && plansRefreshAlert}
            {plansStatus !== null && !olderDataUploaded && (
              <RegeneratePanel onRegenerate={regenerateAll} plansGenerated={plansGenerated} />
            )}
          </PageShell>
        );
      }
      if (olderDataUploaded && generation.phase === 'idle') {
        return <ReportMessage>This Program Evaluation report cannot be generated.</ReportMessage>;
      }
      return (
        <PageShell>
          <GenerationPanel
            phase={generation.phase}
            errorClass={generation.errorClass}
            onGenerate={generateAll}
            onRetry={retryAction}
          />
          {plansRefreshAlert}
        </PageShell>
      );
    }
    return <ReportMessage>This Program Evaluation report cannot be generated.</ReportMessage>;
  }

  return (
    <PageShell>
      <title>Program Evaluation Report</title>
      {report.assessment_data_absent && <AssessmentDataAbsentBanner />}
      {inputsProvider && (newerDataAvailable || generation.phase !== 'idle') && (
        <GenerationPanel
          phase={generation.phase}
          errorClass={generation.errorClass}
          onGenerate={generateAll}
          onRetry={retryAction}
        />
      )}
      {inputsProvider && plansRefreshAlert}
      {inputsProvider &&
        plansStatus !== null &&
        !newerDataAvailable &&
        !olderDataUploaded &&
        (generation.phase === 'idle' || generation.phase === 'ready') && (
          <RegeneratePanel
            onRegenerate={regenerateAll}
            plansGenerated={plansGenerated}
            spacingPosition="bottom"
          />
        )}
      <DistrictHeader>
        <ProgramEvaluationHeader report={report} />
      </DistrictHeader>

      <ScoreUnitProvider initialUnit={content?.unit_mode}>
        <ScoreUnitSync onChange={setGenerationUnit} />
        <ToggleBar>
          <ScoreUnitToggle />
        </ToggleBar>

        <ReportSections report={report} content={content} />
      </ScoreUnitProvider>

      <DataNotes notes={report.data_quality.notes} />
    </PageShell>
  );
}
